import { useEffect, useState } from 'react';

import { CSRF_HEADER, CSRF_VALUE } from './client.ts';
import { kitText } from './messages.ts';

/**
 * Rule Book §3.13 — the target console's side of a platform login-as.
 *
 * AffDice SuperAdmin opens this console in a new tab with `?login_as=<one-time code>`.
 * The code is read once, stripped from the address (anything else in the
 * query, such as a brand console's `brand_id`, stays), and exchanged at the
 * API for the session minted for this console (3.10a). The session lives in
 * this tab's memory only, the way a brand-console tab's does: a reload asks
 * for the code again, which is spent, and lands on the sign-in form.
 *
 * `LoginAsBanner` is §3.13c's banner, shown for the whole session.
 */

/** The code in the address, removed from it; null when there is none. */
export function takeLoginAsCode(loc: Location = location): string | null {
  const params = new URLSearchParams(loc.search);
  const code = params.get('login_as');
  if (!code) return null;
  params.delete('login_as');
  const search = params.toString();
  history.replaceState(null, '', loc.pathname + (search ? `?${search}` : '') + loc.hash);
  return code;
}

/** Spends the code; the session token, or a message saying why not. */
export async function redeemLoginAsCode(
  baseUrl: string,
  code: string,
): Promise<{ readonly token: string } | { readonly error: string }> {
  try {
    const res = await fetch(`${baseUrl.replace(/\/+$/, '')}/v1/login-as/redeem`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    const body = (await res.json().catch(() => ({}))) as { token?: unknown };
    if (res.ok && typeof body.token === 'string') return { token: body.token };
  } catch {
    // Falls through to the same answer: there is nothing more useful to say.
  }
  return { error: kitText('loginAs.linkUsed') };
}

interface Current {
  readonly active: boolean;
  readonly platform_user?: string;
  readonly reason?: string;
  readonly ends_at?: string;
}

/**
 * Who from AffDice SuperAdmin is signed in as this user, why, and until when, with an
 * End button. Renders nothing on an ordinary session.
 */
export function LoginAsBanner({ baseUrl, token }: { baseUrl: string; token?: string | undefined }) {
  const [current, setCurrent] = useState<Current | null>(null);
  const [ending, setEnding] = useState(false);
  const [failed, setFailed] = useState(false);
  const [ended, setEnded] = useState(false);
  const base = baseUrl.replace(/\/+$/, '');
  const headers: Record<string, string> = token
    ? { authorization: `Bearer ${token}` }
    : { [CSRF_HEADER]: CSRF_VALUE };

  useEffect(() => {
    let live = true;
    fetch(`${base}/v1/login-as/current`, { headers, credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { active: false }))
      .then((b: Current) => { if (live) setCurrent(b); })
      .catch(() => undefined);
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, token]);

  if (ended) {
    return <div className="impersonation-banner" role="status">{kitText('loginAs.ended')}</div>;
  }
  if (!current?.active) return null;

  const ends = current.ends_at
    ? new Date(current.ends_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    : '';
  const end = () => {
    setEnding(true);
    setFailed(false);
    fetch(`${base}/v1/login-as/end`, {
      method: 'POST',
      headers: { ...headers, 'content-type': 'application/json', [CSRF_HEADER]: CSRF_VALUE },
      credentials: 'include',
      body: '{}',
    })
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        setEnded(true);
      })
      .catch(() => setFailed(true))
      .finally(() => setEnding(false));
  };

  return (
    <div className="impersonation-banner login-as-banner" role="status">
      <span>
        {kitText('loginAs.banner', {
          who: current.platform_user ?? '',
          reason: current.reason ?? '',
          ends,
        })}
      </span>
      <button type="button" className="secondary" onClick={end} disabled={ending}>
        {ending ? kitText('loginAs.ending') : kitText('loginAs.end')}
      </button>
      {failed ? <span role="alert">{kitText('loginAs.endFailed')}</span> : null}
    </div>
  );
}

const redeeming = new Map<string, Promise<{ readonly token: string } | { readonly error: string }>>();

export type LoginAsHandoff =
  | { readonly state: 'none' }
  | { readonly state: 'redeeming' }
  | { readonly state: 'ready'; readonly token: string }
  | { readonly state: 'failed'; readonly error: string };

/**
 * The boot-time half: a tab opened from AffDice SuperAdmin spends its code once (the
 * promise is shared, so React's double-run of effects in development cannot
 * spend it twice) and answers the session to use. `none` for every other tab,
 * which then boots as it always has.
 */
export function useLoginAsHandoff(baseUrl: string): LoginAsHandoff {
  const [code] = useState(() => takeLoginAsCode());
  const [state, setState] = useState<LoginAsHandoff>(code ? { state: 'redeeming' } : { state: 'none' });
  useEffect(() => {
    if (!code) return;
    let promise = redeeming.get(code);
    if (!promise) {
      promise = redeemLoginAsCode(baseUrl, code);
      redeeming.set(code, promise);
    }
    let live = true;
    void promise.then((r) => {
      if (!live) return;
      setState('token' in r ? { state: 'ready', token: r.token } : { state: 'failed', error: r.error });
    });
    return () => { live = false; };
  }, [baseUrl, code]);
  return state;
}
